import { Link } from "react-router-dom";
import { Car, Home as HomeIcon, HeartPulse, Award, Users, Handshake, ChevronRight, FileText, Download, Activity, Briefcase, Bike, Zap, Plane, PawPrint } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const hero = "https://images.pexels.com/photos/28460175/pexels-photo-28460175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const aboutImg = "https://customer-assets.emergentagent.com/job_auto-home-quotes/artifacts/gnmyv8zr_FOTO%20SISTEMATA.png";

function Stat({ value, label }) {
  return (
    <div className="border-l border-slate-200 pl-4 sm:pl-5">
      <div className="font-serif text-3xl sm:text-4xl text-slate-900 leading-none">{value}</div>
      <div className="overline mt-2 text-[10px] sm:text-xs leading-tight">{label}</div>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, items, image, href, ctaTestid }) {
  return (
    <div
      data-testid={`service-card-${ctaTestid}`}
      className="group bg-white border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col"
    >
      <Link to={href} className="block aspect-[16/10] overflow-hidden" data-testid={`service-image-${ctaTestid}`}>
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      </Link>
      <div className="p-7 md:p-8 flex-1 flex flex-col">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 flex items-center justify-center bg-slate-900 text-[#C5A059] shrink-0">
            <Icon size={18} />
          </span>
          <h3 className="font-serif text-xl md:text-2xl text-slate-900 leading-tight">{title}</h3>
        </div>
        <ul className="mt-5 space-y-2 text-sm text-slate-600 flex-1">
          {items.map((it) => (
            <li key={it} className="flex gap-2">
              <span className="text-[#C5A059] shrink-0">—</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
        <Link
          to={href}
          data-testid={`service-cta-${ctaTestid}`}
          className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-slate-900 hover:text-[#C5A059] transition-colors"
        >
          Richiedi preventivo <ChevronRight size={16} />
        </Link>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <h1
              data-testid="hero-title"
              className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-slate-900"
            >
              Sicurezza
              <br />
              <span className="italic text-slate-800">al tuo fianco.</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              Dal 1958 la nostra agenzia plurimandataria è
              sinonimo di affidabilità e competenza nel mondo delle assicurazioni.
              Confrontiamo le soluzioni delle migliori compagnie e costruiamo la
              polizza più adatta a te. Inserisci le informazioni principali della
              copertura che ti interessa e, se vuoi, allega i documenti utili:
              analizzeremo la richiesta e ti ricontatteremo con una proposta
              personalizzata.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/preventivo"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-sm tracking-wide transition-colors"
              >
                Richiedi un preventivo <ChevronRight size={16} />
              </Link>
              <a
                href="#servizi"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white h-12 px-7 text-sm tracking-wide transition-colors"
              >
                Scopri i nostri servizi
              </a>
            </div>

            <div className="mt-14 hidden sm:grid sm:grid-cols-3 gap-6">
              <Stat value="1958" label="Anno di fondazione" />
              <Stat value="3" label="Generazioni al tuo fianco" />
              <Stat value="24/48h" label="Risposta preventivi" />
            </div>
          </div>

          <div className="hidden md:block md:col-span-6 relative">
            <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden">
              <img src={hero} alt="Villa italiana classica" className="w-full h-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-slate-200"></div>
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-white border border-slate-200 p-6 w-64 shadow-xl">
              <div className="overline">Garanzia</div>
              <div className="mt-2 font-serif text-xl text-slate-900">Assistenza dedicata</div>
              <p className="mt-2 text-xs text-slate-500">Un referente unico per tutte le tue polizze.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section id="servizi" className="bg-white border-y border-slate-200 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="overline">I nostri servizi</div>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900 max-w-2xl">
                Coperture pensate per ogni esigenza.
              </h2>
            </div>
            <div className="divider-gold"></div>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <ServiceCard
              icon={Car}
              title="Assicurazione Auto"
              image="https://images.pexels.com/photos/13861/IMG_3496bfree.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/assicurazione-auto-milano"
              ctaTestid="auto"
              items={[
                "RC Auto + garanzie accessorie",
                "Furto, incendio, cristalli, kasko",
                "Assistenza stradale h24",
                "Tutela legale e infortuni conducente",
              ]}
            />
            <ServiceCard
              icon={Bike}
              title="Assicurazione Moto"
              image="https://images.pexels.com/photos/1413412/pexels-photo-1413412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/preventivo?type=moto"
              ctaTestid="moto"
              items={[
                "RCA moto e scooter",
                "Furto e incendio",
                "Assistenza stradale",
                "Infortuni conducente",
              ]}
            />
            <ServiceCard
              icon={Zap}
              title="Monopattino Elettrico"
              image="https://images.pexels.com/photos/8769465/pexels-photo-8769465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/assicurazione-monopattino"
              ctaTestid="monopattino"
              items={[
                "RC obbligatoria per legge",
                "Tutela legale",
                "Furto del mezzo",
                "Assistenza in strada",
              ]}
            />
            <ServiceCard
              icon={HomeIcon}
              title="Assicurazione Casa"
              image="https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/assicurazione-casa"
              ctaTestid="casa"
              items={[
                "Incendio ed eventi naturali",
                "Furto, rapina e atti vandalici",
                "Responsabilità civile del capofamiglia",
                "Protezione impianti e contenuto",
              ]}
            />
            <ServiceCard
              icon={HeartPulse}
              title="Polizza Vita"
              image="https://images.pexels.com/photos/3818963/pexels-photo-3818963.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/polizza-vita"
              ctaTestid="vita"
              items={[
                "Protezione della famiglia (TCM)",
                "Previdenza integrativa (FPA, PIP)",
                "Investimento Multiramo",
                "Soluzioni business (TFM, TFR)",
              ]}
            />
            <ServiceCard
              icon={Activity}
              title="Infortuni e Malattia"
              image="https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/infortuni-malattia"
              ctaTestid="infortuni"
              items={[
                "Invalidità permanente da infortunio",
                "Rimborso spese mediche",
                "Indennità da ricovero",
                "Patologie gravi e prevenzione",
              ]}
            />
            <ServiceCard
              icon={Briefcase}
              title="RC Professionale"
              image="https://images.pexels.com/photos/7691679/pexels-photo-7691679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/rc-professionale"
              ctaTestid="rc-pro"
              items={[
                "Responsabilità civile per professionisti",
                "Copertura errori e omissioni",
                "Tutela legale professionale",
                "Retroattività e ultrattività",
              ]}
            />
            <ServiceCard
              icon={Plane}
              title="Assicurazione Viaggi"
              image="https://images.pexels.com/photos/346885/pexels-photo-346885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/preventivo?type=viaggi"
              ctaTestid="viaggi"
              items={[
                "Annullamento viaggio",
                "Spese mediche all'estero",
                "Bagaglio e assistenza",
                "Responsabilità civile in viaggio",
              ]}
            />
            <ServiceCard
              icon={PawPrint}
              title="Cani e Gatti"
              image="https://images.pexels.com/photos/1108099/pexels-photo-1108099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              href="/assicurazione-cani-gatti"
              ctaTestid="pet"
              items={[
                "Spese veterinarie",
                "Responsabilità civile del proprietario",
                "Assistenza h24",
                "Tutela legale",
              ]}
            />
          </div>
        </div>
      </section>

      {/* CHI SIAMO */}
      <section id="chi-siamo" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="overline">Chi siamo · Dal 1958</div>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900">
              Proteggiamo il tuo futuro da tre generazioni.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Dal 1958, la nostra <strong>agenzia plurimandataria</strong> è
              sinonimo di affidabilità e competenza nel mondo delle assicurazioni.
              Nata dalla passione e dalla dedizione della nostra famiglia,
              abbiamo attraversato tre generazioni, costruendo un percorso
              fatto di fiducia e relazioni durature con i nostri clienti.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Essere plurimandatari significa poter confrontare le proposte
              delle migliori compagnie e mettere la nostra profonda conoscenza
              del settore al servizio delle tue esigenze. Sceglierci significa
              affidarsi a un partner solido, con radici forti nel tempo e uno
              sguardo attento al futuro, pronto a proteggere ciò che per te
              conta di più.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-6">
              <div>
                <Award size={22} className="text-[#C5A059]" />
                <div className="mt-3 font-serif text-lg text-slate-900">Esperienza</div>
                <p className="text-sm text-slate-500 mt-1">Dal 1958 al servizio di famiglie e imprese.</p>
              </div>
              <div>
                <Users size={22} className="text-[#C5A059]" />
                <div className="mt-3 font-serif text-lg text-slate-900">Ascolto</div>
                <p className="text-sm text-slate-500 mt-1">Preventivi studiati caso per caso.</p>
              </div>
              <div>
                <Handshake size={22} className="text-[#C5A059]" />
                <div className="mt-3 font-serif text-lg text-slate-900">Fiducia</div>
                <p className="text-sm text-slate-500 mt-1">Assistenza reale anche in caso di sinistro.</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="aspect-[5/6] overflow-hidden">
              <img src={aboutImg} alt="Consulenza assicurativa" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline" style={{ color: "#C5A059" }}>Il tuo preventivo, senza impegno</div>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl leading-tight">
              Raccontaci cosa vuoi proteggere.
              <br />Riceviamo, analizziamo e ti rispondiamo.
            </h2>
            <p className="mt-4 text-slate-300 text-sm md:text-base max-w-xl">
              Auto, casa, famiglia, professione e molto altro: inviaci la tua richiesta e ti aiutiamo a trovare la soluzione più adatta.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/preventivo"
              data-testid="cta-section-button"
              className="inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#B08D4C] text-slate-900 h-12 px-8 text-sm tracking-wide transition-colors"
            >
              Inizia il preventivo <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* COMPLIANCE — documenti scaricabili */}
      <section id="compliance" className="bg-white border-y border-slate-200 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="overline">Compliance</div>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl tracking-tight text-slate-900 max-w-2xl">
                Trasparenza e documenti.
              </h2>
              <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed">
                In ottemperanza alla normativa vigente, mettiamo a disposizione i documenti
                precontrattuali, l'informativa privacy e la modulistica utile in caso di sinistro.
              </p>
            </div>
            <div className="divider-gold"></div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/compliance"
              data-testid="compliance-cta"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-sm tracking-wide transition-colors"
            >
              <FileText size={16} /> Consulta i documenti
            </Link>
            <p className="text-sm text-slate-500">Privacy · MUP NO IBIP · MUP IBIP · CID · dati intermediario</p>
          </div>
        </div>
      </section>

      {/* CONTATTI */}
      <section id="contatti" className="py-16 sm:py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">Contatti</div>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl text-slate-900">Parliamone.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              Scrivici via email per informazioni, richieste specifiche o per
              fissare un appuntamento in agenzia. Rispondiamo entro 24/48 ore
              lavorative.
            </p>
          </div>
          <div className="md:col-span-7 bg-white border border-slate-200 p-5 sm:p-6 md:p-8">
            <ul className="space-y-5">
              <li className="flex items-start gap-3 sm:gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center bg-slate-900 text-[#C5A059]">✉</span>
                <div className="min-w-0 flex-1">
                  <div className="overline">Email</div>
                  <a href="mailto:info@dapraassicurazioni.it" className="font-serif text-base sm:text-lg md:text-xl text-slate-900 hover:text-[#C5A059] break-all">
                    info@dapraassicurazioni.it
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center bg-slate-900 text-[#C5A059]">⚖</span>
                <div className="min-w-0 flex-1">
                  <div className="overline">PEC</div>
                  <a href="mailto:dapraassicurazioni@propec.it" className="font-serif text-base sm:text-lg md:text-xl text-slate-900 hover:text-[#C5A059] break-all">
                    dapraassicurazioni@propec.it
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center bg-slate-900 text-[#C5A059]">☎</span>
                <div className="min-w-0 flex-1">
                  <div className="overline">Telefono</div>
                  <a href="tel:+390220177" className="font-serif text-lg md:text-xl text-slate-900 hover:text-[#C5A059]">+39 02 20 17 77</a>
                </div>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center bg-slate-900 text-[#C5A059]">◎</span>
                <div className="min-w-0 flex-1">
                  <div className="overline">Agenzia</div>
                  <div className="font-serif text-base sm:text-lg md:text-xl text-slate-900 break-words">Via Lambro, 4 — 20129 Milano</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
